import { Fragment, useMemo, type CSSProperties, type ReactNode } from 'react'
import type { Block, MarginNoteBlock, PinSide, UnfoldBlock, VoiceBlock } from '../../lib/types'
import { isPinnedNote, pinnedRanges, pinSide } from '../../lib/blocks'
import { isNarrated } from '../../lib/narration'
import { blockText } from '../../lib/text'
import { cellMarginStyle, columnsGridStyle, columnWidths, equalWidths } from '../../lib/columns'
import { layoutBlocks, type FlowSegment, type Row, type Side } from './blockOps'

/** A small chip in the left gutter beside a block: a narration mark, or a recorded piece. */
export interface GutterChip {
  /** "0:14" */
  label: string
  /** Recording into it right now (a pulsing dot, not a button). */
  live?: boolean
  onClick?: () => void
  /** Accessible name for onClick, e.g. "Listen from 0:14". */
  clickLabel?: string
  onRemove?: () => void
  /** Accessible name for onRemove, e.g. "Clear the mark at 0:14". */
  removeLabel?: string
}

/**
 * What the audio dock (record, listen, sync, voice memo) asks of the page.
 * While `locked`, editing is off and margins can be read but not touched;
 * narrated blocks become tap targets when `tap` is set.
 */
export interface DockView {
  locked: boolean
  /** The block being read or recorded now (tinted rose). */
  currentId: string | null
  tap?: {
    /** Accessible name for a block's tap target, from a snippet of its text. */
    label(snippet: string, blockId: string): string
    onTap(blockId: string): void
  }
  chips: ReadonlyMap<string, GutterChip>
}

export const NO_DOCK: DockView = { locked: false, currentId: null, chips: new Map() }

interface Props {
  blocks: Block[]
  /** Remount counters: a block's editable remounts when its html changes from code. */
  rev: Record<string, number>
  dock: DockView
  /** `covered`: the side of the pinned note it's under (its dotted line goes on that side), or null. */
  renderColumn: (block: Block, opts: { covered: PinSide | null; inCell: boolean }) => ReactNode
  renderAside: (block: MarginNoteBlock | VoiceBlock) => ReactNode
  /** A pinned margin note, in the sticky track beside its range (or inline, with nothing to stay beside). */
  renderPin: (pin: MarginNoteBlock) => ReactNode
  renderUnfold: (block: UnfoldBlock) => ReactNode
  /** Menus and forms that open under a block (the "/" menu, the "+", pickers, the drop line). */
  renderExtras: (blockId: string) => ReactNode
  /** The gutter between two cells of a Columns group (drag to resize; a drop target). */
  renderGutter?: (gutter: { startId: string; markerId: string; boundary: number; widths: number[] }) => ReactNode
  /** Just outside a block's free edges: a way to add a column there. */
  renderBeside?: (blockId: string, sides: Side[]) => ReactNode
}

const BOTH: Side[] = ['left', 'right']
const NONE: Side[] = []

/**
 * React 18 (the runtime here) doesn't know a boolean `inert` prop, so it is
 * set as an attribute: locked content stays on screen, out of reach.
 */
const inertWhen = (locked: boolean) => (el: HTMLElement | null) => {
  el?.toggleAttribute('inert', locked)
}

/**
 * The document in the Composer v4 layout: each column block with its margin
 * notes and voice memos in the right margin, its unfold cards underneath,
 * and each pin's card in a sticky track beside the blocks it covers. The
 * margin CSS (.wr-anchor / .wr-aside-* in index.css) folds all of it inline
 * when the page is too narrow for margins.
 */
export function BlockList({ blocks, rev, dock, renderColumn, renderAside, renderPin, renderUnfold, renderExtras, renderGutter, renderBeside }: Props) {
  const segments = useMemo(() => layoutBlocks(blocks), [blocks])
  const covered = useMemo(() => {
    const out = new Map<string, PinSide>()
    const ranges = pinnedRanges(blocks)
    for (const b of blocks) if (isPinnedNote(b)) for (const id of ranges.get(b.id) ?? []) out.set(id, pinSide(b))
    return out
  }, [blocks])
  const lock = inertWhen(dock.locked)
  const startOf = (id: string) => blocks.find((b) => b.id === id)

  // `sides`: the edges with page beside them (in columns, only the outer edges of the outer cells).
  const renderRow = (row: Row, i: number, inCell = false, sides: Side[] = BOTH) => {
    const b = row.block
    const id = b?.id ?? `lead-${i}`
    const narrated = !!b && isNarrated(b)
    const chip = b ? dock.chips.get(b.id) : undefined
    const current = !!b && dock.currentId === b.id
    return (
      <div
        key={`${id}:${rev[id] ?? 0}`}
        data-block-id={id}
        data-row={b ? b.id : undefined}
        data-current={current ? '' : undefined}
        className={`wr-anchor group/row rounded-[6px] transition-[background-color,box-shadow,padding] duration-300 ${
          current ? 'bg-accent/14 shadow-[0_0_0_8px_color-mix(in_srgb,var(--accent)_14%,transparent)]' : ''
        }`}
      >
        {b && chip && <ChipView chip={chip} />}
        {b && (
          <div ref={lock} className="contents">
            {renderColumn(b, { covered: covered.get(b.id) ?? null, inCell })}
          </div>
        )}
        {b && !dock.locked && sides.length > 0 && renderBeside?.(b.id, sides)}
        {b && dock.tap && narrated && (
          <button
            type="button"
            aria-label={dock.tap.label(snippet(b), b.id)}
            aria-current={current ? 'true' : undefined}
            onClick={(e) => {
              e.stopPropagation()
              dock.tap?.onTap(b.id)
            }}
            className="absolute inset-0 z-10 cursor-pointer rounded-[6px] outline-none hover:bg-accent/10 focus-visible:ring-2 focus-visible:ring-accent"
          />
        )}
        {row.asides.length > 0 && (
          <div ref={lock} className={`wr-aside-right flex flex-col gap-5 transition-opacity ${dock.locked ? 'opacity-70' : ''}`}>
            {row.asides.map((a) => (
              <Fragment key={a.id}>{renderAside(a)}</Fragment>
            ))}
          </div>
        )}
        {row.loosePins.length > 0 && (
          <div ref={lock} className="contents">
            {row.loosePins.map((p) => (
              <div key={p.id} className="mb-[22px]">
                {renderPin(p)}
              </div>
            ))}
          </div>
        )}
        {row.unfolds.length > 0 && (
          <div ref={lock} className="contents">
            {row.unfolds.map((u) => (
              <Fragment key={u.id}>{renderUnfold(u)}</Fragment>
            ))}
          </div>
        )}
        {b && !dock.locked && renderExtras(b.id)}
      </div>
    )
  }

  const renderFlow = (seg: FlowSegment, i: number, inCell: boolean, sides: Side[] = BOTH) =>
    seg.kind === 'row' ? (
      renderRow(seg.row, i, inCell, sides)
    ) : (
      // A pin on the right takes that margin for its range: the ordinary notes there fold under their paragraphs (.wr-pin-right).
      <div
        key={`pin:${seg.pin.id}:${rev[seg.pin.id] ?? 0}`}
        className={`wr-anchor ${pinSide(seg.pin) === 'right' ? 'wr-pin-right' : ''}`}
        data-pin-range={seg.pin.id}
      >
        <div
          ref={lock}
          data-block-id={seg.pin.id}
          className={`${pinSide(seg.pin) === 'right' ? 'wr-aside-right-track' : 'wr-aside-left-track'} wr-pin-track transition-opacity ${dock.locked ? 'opacity-70' : ''}`}
        >
          {renderPin(seg.pin)}
        </div>
        {seg.rows.map((row, j) => renderRow(row, i * 1000 + j, inCell, sides))}
      </div>
    )

  return (
    <>
      {segments.map((seg, i) =>
        seg.kind !== 'columns' ? (
          renderFlow(seg, i, false)
        ) : (
          // Columns: the cells side by side (stacked when the page is narrow, see .wr-cols), each its own little document.
          <div key={`cols:${seg.startId}:${rev[seg.startId] ?? 0}`} data-cols={seg.startId} data-block-id={seg.startId} data-focus-unit="" className="wr-cols group/cols">
            <div className="wr-cols-grid" data-n={seg.cells.length} style={columnsGridStyle(startOf(seg.startId), seg.cells.length) as CSSProperties}>
              {seg.cells.map((cell, c) => {
                const n = seg.cells.length
                const sides = n === 1 ? BOTH : c === 0 ? BOTH.slice(0, 1) : c === n - 1 ? BOTH.slice(1) : NONE
                return (
                  <div key={cell.markerId} className="wr-cell" data-cell={c + 1} style={cellMarginStyle(startOf(seg.startId), n, c) as CSSProperties}>
                    {/* The gutter before this cell: drag it to resize the two either side. */}
                    {c > 0 &&
                      !dock.locked &&
                      renderGutter?.({
                        startId: seg.startId,
                        markerId: cell.markerId,
                        boundary: c,
                        widths: columnWidths(startOf(seg.startId), n) ?? equalWidths(n),
                      })}
                    {/* The top of the cell, as a drop target (after its marker). */}
                    <div data-drop-after={cell.markerId} aria-hidden className="h-2" />
                    {!dock.locked && renderExtras(cell.markerId)}
                    {cell.segments.map((s, j) => renderFlow(s, i * 100000 + c * 1000 + j, true, sides))}
                  </div>
                )
              })}
            </div>
            {/* Under the whole group: where an image dragged over it will land (images wrap, never join columns). */}
            {!dock.locked && renderExtras(seg.endId)}
          </div>
        ),
      )}
    </>
  )
}

/** A mark or a piece, in the left gutter when there are margins, else just above its block. */
function ChipView({ chip }: { chip: GutterChip }) {
  const dot = <span aria-hidden className={`size-1.5 rounded-full ${chip.live ? 'animate-pulse bg-accent/15' : 'bg-accent/15'}`} />
  return (
    <span className="wr-sync-chip mb-1.5 inline-flex items-center gap-1 rounded-full bg-primary py-0.5 pl-2 pr-1 font-sans text-[10.5px] font-semibold tabular-nums text-primary-foreground">
      {chip.onClick ? (
        <button
          type="button"
          aria-label={chip.clickLabel ?? chip.label}
          onClick={(e) => {
            e.stopPropagation()
            chip.onClick?.()
          }}
          className="flex items-center gap-1 pr-1 hover:text-accent"
        >
          {dot}
          {chip.label}
        </button>
      ) : (
        <span className="flex items-center gap-1 pr-1">
          {dot}
          {chip.label}
        </span>
      )}
      {chip.onRemove && (
        <button
          type="button"
          aria-label={chip.removeLabel ?? 'Remove'}
          onClick={(e) => {
            e.stopPropagation()
            chip.onRemove?.()
          }}
          className="-mr-1 -ml-1 flex size-4 items-center justify-center rounded-full text-[10px] leading-none text-primary-foreground/80 hover:bg-primary-foreground/15 hover:text-primary-foreground"
        >
          ✕
        </button>
      )}
    </span>
  )
}

function snippet(b: Block): string {
  const t = blockText(b).trim()
  return t.length > 48 ? `${t.slice(0, 46)}…` : t
}
